import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const TOKEN_KEY = 'admin_token';
const REFRESH_KEY = 'admin_refresh';
const EXPIRES_KEY = 'admin_expires';
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';
const REFRESH_BEFORE_MS = 5 * 60 * 1000;

interface User {
  id: string;
  email: string | undefined;
  role: 'admin' | 'superadmin' | null;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  loading: boolean;
  role: 'admin' | 'superadmin' | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  token: null,
  loading: true,
  role: null,
  signIn: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const doRefreshRef = useRef<() => Promise<void>>(async () => {});

  const clearAuth = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(EXPIRES_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const scheduleRefresh = useCallback((expiresAt: number) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const ms = expiresAt * 1000 - Date.now() - REFRESH_BEFORE_MS;
    if (ms > 0) {
      timerRef.current = setTimeout(() => doRefreshRef.current(), ms);
    }
  }, []);

  const doRefresh = useCallback(async () => {
    const storedRefresh = localStorage.getItem(REFRESH_KEY);
    if (!storedRefresh) { clearAuth(); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: storedRefresh }),
      });
      const json = (await res.json()) as {
        success: boolean;
        data: { access_token: string; refresh_token: string; expires_at: number };
      };
      if (!res.ok || !json.success) { clearAuth(); return; }
      localStorage.setItem(TOKEN_KEY, json.data.access_token);
      localStorage.setItem(REFRESH_KEY, json.data.refresh_token);
      localStorage.setItem(EXPIRES_KEY, String(json.data.expires_at));
      setToken(json.data.access_token);
      scheduleRefresh(json.data.expires_at);
    } catch {
      clearAuth();
    }
  }, [clearAuth, scheduleRefresh]);

  useEffect(() => { doRefreshRef.current = doRefresh; }, [doRefresh]);

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    const storedRefresh = localStorage.getItem(REFRESH_KEY);
    const expiresAt = Number(localStorage.getItem(EXPIRES_KEY) ?? 0);

    if (!stored) { setLoading(false); return; }

    const nearExpiry = expiresAt > 0 && Date.now() >= expiresAt * 1000 - REFRESH_BEFORE_MS;

    if (nearExpiry && storedRefresh) {
      doRefresh().finally(() => setLoading(false));
      return;
    }

    fetch(`${API_BASE_URL}/api/v1/admin/auth/me`, {
      headers: { Authorization: `Bearer ${stored}` },
    })
      .then((res) => res.json() as Promise<{
        success: boolean;
        data: { id: string; email: string; role: string | null };
      }>)
      .then(({ success, data }) => {
        if (success) {
          setToken(stored);
          setUser({ id: data.id, email: data.email, role: (data.role as User['role']) ?? null });
          if (expiresAt) scheduleRefresh(expiresAt);
        } else {
          clearAuth();
        }
      })
      .catch(() => clearAuth())
      .finally(() => setLoading(false));
  }, []);

  const signIn = async (email: string, password: string) => {
    const res = await fetch(`${API_BASE_URL}/api/v1/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const json = (await res.json()) as {
      success: boolean;
      data: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
        user: { id: string; email: string; role: string | null };
      };
      error?: { message: string };
    };
    if (!res.ok || !json.success) {
      throw new Error(json.error?.message ?? 'Login failed');
    }
    localStorage.setItem(TOKEN_KEY, json.data.access_token);
    localStorage.setItem(REFRESH_KEY, json.data.refresh_token);
    localStorage.setItem(EXPIRES_KEY, String(json.data.expires_at));
    setToken(json.data.access_token);
    setUser({ id: json.data.user.id, email: json.data.user.email, role: (json.data.user.role as User['role']) ?? null });
    scheduleRefresh(json.data.expires_at);
  };

  const signOut = async () => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) {
      await fetch(`${API_BASE_URL}/api/v1/admin/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${stored}` },
      }).catch(() => {});
    }
    clearAuth();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, role: user?.role ?? null, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
